import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, User, Baby, Users, Briefcase, ArrowLeft, ArrowRight, Cake, Snowflake, Gem, Flower2,
  Home as HomeIcon, GraduationCap, HandHeart, Gift, Wrench, Crown, PartyPopper, Star, Ticket,
  PenLine, Wand2, Loader2, Check,
} from "lucide-react";

const RECIPIENTS = [
  { v: "partner", l: "Partner", icon: Heart },
  { v: "mum", l: "Mum", icon: User },
  { v: "dad", l: "Dad", icon: User },
  { v: "brother", l: "Brother", icon: User },
  { v: "sister", l: "Sister", icon: User },
  { v: "friend", l: "Friend", icon: Users },
  { v: "child", l: "Child", icon: Baby },
  { v: "colleague", l: "Colleague", icon: Briefcase },
];

const OCCASIONS = [
  { v: "birthday", l: "Birthday", icon: Cake },
  { v: "christmas", l: "Christmas", icon: Snowflake },
  { v: "anniversary", l: "Anniversary", icon: Gem },
  { v: "wedding", l: "Wedding", icon: Heart },
  { v: "mothers_day", l: "Mother's Day", icon: Flower2 },
  { v: "fathers_day", l: "Father's Day", icon: Flower2 },
  { v: "valentines", l: "Valentine's", icon: Heart },
  { v: "housewarming", l: "Housewarming", icon: HomeIcon },
  { v: "graduation", l: "Graduation", icon: GraduationCap },
  { v: "thank_you", l: "Thank you", icon: HandHeart },
  { v: "just_because", l: "Just because", icon: Gift },
];

const BUDGETS = [
  { v: "u25", l: "Under £25" },
  { v: "25_50", l: "£25 – £50" },
  { v: "50_100", l: "£50 – £100" },
  { v: "100_250", l: "£100 – £250" },
  { v: "250p", l: "£250+" },
];

const VIBES = [
  { v: "practical", l: "Practical", icon: Wrench },
  { v: "luxurious", l: "Luxurious", icon: Crown },
  { v: "fun", l: "Fun", icon: PartyPopper },
  { v: "memorable", l: "Memorable", icon: Star },
  { v: "experience", l: "Experience", icon: Ticket },
  { v: "personalised", l: "Personalised", icon: PenLine },
  { v: "unusual", l: "Unusual", icon: Wand2 },
];

const URGENCY = [
  { v: "no_rush", l: "No rush" },
  { v: "week", l: "Within a week" },
  { v: "3days", l: "Within 3 days" },
  { v: "tomorrow", l: "By tomorrow" },
];

export default function Quiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [recipient, setRecipient] = useState("");
  const [recipientOther, setRecipientOther] = useState("");
  const [occasion, setOccasion] = useState("");
  const [budgetPreset, setBudgetPreset] = useState("");
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [about, setAbout] = useState("");
  const [vibe, setVibe] = useState([]);
  const [avoid, setAvoid] = useState("");
  const [urgency, setUrgency] = useState("");

  useEffect(() => {
    api.post("/quiz/start").then((r) => setSessionId(r.data.session_id)).catch(() => {});
  }, []);

  const toggleVibe = (v) => {
    setVibe((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const steps = useMemo(() => [
    { key: "recipient", title: "Who are you buying for?", sub: "Pick the person you have in mind.", optional: false },
    { key: "occasion", title: "What's the occasion?", sub: "This helps us set the right tone.", optional: false },
    { key: "budget", title: "What's your budget?", sub: "Choose a range or set your own.", optional: false },
    { key: "about", title: "Tell us about them", sub: "The more you share, the better the matches. e.g. \"Dad is 54, loves golf and cooking, hates clutter\".", optional: false },
    { key: "vibe", title: "What kind of gift would they prefer?", sub: "Choose as many as feel right.", optional: false },
    { key: "avoid", title: "Anything to avoid?", sub: "Optional — tell us what to steer clear of.", optional: true },
    { key: "urgency", title: "When do you need it?", sub: "Optional — helps us keep it realistic.", optional: true },
  ], []);

  const total = steps.length;
  const current = steps[step];

  const validate = () => {
    setError("");
    const k = current.key;
    if (k === "recipient" && !recipient) return "Please choose who this is for.";
    if (k === "recipient" && recipient === "other" && !recipientOther.trim()) return "Tell us who it's for.";
    if (k === "occasion" && !occasion) return "Please pick an occasion.";
    if (k === "budget") {
      if (customMode) {
        const lo = parseFloat(customMin), hi = parseFloat(customMax);
        if (isNaN(lo) || isNaN(hi)) return "Enter both a minimum and maximum.";
        if (lo < 0 || hi < 0) return "Budget can't be negative.";
        if (lo >= hi) return "Minimum must be less than maximum.";
      } else if (!budgetPreset) {
        return "Choose a budget range.";
      }
    }
    if (k === "about" && about.trim().length < 3) return "A few words about them helps a lot.";
    if (k === "vibe" && vibe.length === 0) return "Pick at least one style (or 'Not sure' below).";
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (step < total - 1) setStep(step + 1);
    else submit();
  };

  const back = () => {
    setError("");
    if (step > 0) setStep(step - 1);
    else navigate("/");
  };

  const buildAnswers = () => {
    const recVal = recipient === "other" ? recipientOther.trim() : recipient;
    const budget = customMode
      ? { type: "custom", min: parseFloat(customMin), max: parseFloat(customMax) }
      : { type: "preset", preset: budgetPreset };
    const budgetLabel = customMode
      ? `£${customMin} – £${customMax}`
      : (BUDGETS.find((b) => b.v === budgetPreset)?.l || "");
    return {
      recipient: recVal,
      occasion,
      budget,
      budget_preset: customMode ? null : budgetPreset,
      budget_label: budgetLabel,
      about: about.trim(),
      vibe: vibe.filter((v) => v !== "not_sure"),
      interests: [],
      avoid: avoid.trim(),
      urgency,
    };
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const answers = buildAnswers();
      const res = await api.post("/recommend", { answers, session_id: sessionId });
      sessionStorage.setItem("gm_result", JSON.stringify({ ...res.data, answers }));
      navigate("/results");
    } catch (e) {
      setError("Something went wrong finding gifts. Please try again.");
      toast.error("Could not generate recommendations");
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / total) * 100;

  return (
    <div className="min-h-[calc(100vh-64px)] max-w-3xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
      <div className="flex items-center justify-between mb-2 text-sm text-muted-foreground">
        <span className="font-mono uppercase tracking-widest text-xs">Step {step + 1} of {total}</span>
        {current.optional && <span className="text-xs">Optional</span>}
      </div>
      <Progress value={progress} data-testid="quiz-step-progress-bar" className="h-2 bg-brand-cream2 mb-8 [&>div]:bg-brand-green" />

      <div key={step} className="bg-white/90 backdrop-blur-md rounded-2xl border border-brand-cream2 shadow-xl p-6 sm:p-10 gm-fade-up">
        <h1 className="font-serif text-2xl sm:text-3xl mb-2" data-testid="quiz-question-title">{current.title}</h1>
        <p className="text-muted-foreground mb-7">{current.sub}</p>

        {current.key === "recipient" && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {RECIPIENTS.map((r) => (
                <SelectTile key={r.v} active={recipient === r.v} onClick={() => setRecipient(r.v)}
                  icon={r.icon} label={r.l} testid={`recipient-${r.v}`} />
              ))}
              <SelectTile active={recipient === "other"} onClick={() => setRecipient("other")}
                icon={Users} label="Other" testid="recipient-other" />
            </div>
            {recipient === "other" && (
              <Input className="mt-4" placeholder="Who is it for?" data-testid="recipient-other-input"
                value={recipientOther} onChange={(e) => setRecipientOther(e.target.value)} />
            )}
          </div>
        )}

        {current.key === "occasion" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {OCCASIONS.map((o) => (
              <SelectTile key={o.v} active={occasion === o.v} onClick={() => setOccasion(o.v)}
                icon={o.icon} label={o.l} testid={`occasion-${o.v}`} />
            ))}
          </div>
        )}

        {current.key === "budget" && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {BUDGETS.map((b) => (
                <button key={b.v} data-testid={`budget-${b.v}`}
                  onClick={() => { setBudgetPreset(b.v); setCustomMode(false); }}
                  className={`h-14 rounded-xl border font-mono text-sm font-semibold transition-all ${!customMode && budgetPreset === b.v ? "border-brand-green bg-brand-green text-brand-cream" : "border-brand-cream2 bg-white hover:border-brand-green/40"}`}>
                  {b.l}
                </button>
              ))}
              <button data-testid="budget-custom"
                onClick={() => setCustomMode(true)}
                className={`h-14 rounded-xl border font-medium text-sm transition-all ${customMode ? "border-brand-green bg-brand-green text-brand-cream" : "border-brand-cream2 bg-white hover:border-brand-green/40"}`}>
                Custom range
              </button>
            </div>
            {customMode && (
              <div className="mt-4 flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Min (£)</label>
                  <Input type="number" min="0" data-testid="budget-custom-min" value={customMin}
                    onChange={(e) => setCustomMin(e.target.value)} placeholder="0" />
                </div>
                <span className="text-muted-foreground mt-5">–</span>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Max (£)</label>
                  <Input type="number" min="0" data-testid="budget-custom-max" value={customMax}
                    onChange={(e) => setCustomMax(e.target.value)} placeholder="200" />
                </div>
              </div>
            )}
          </div>
        )}

        {current.key === "about" && (
          <Textarea rows={5} data-testid="about-input" value={about} onChange={(e) => setAbout(e.target.value)}
            placeholder="e.g. Mum is 60, loves gardening and a good crime novel, always cold, hates clutter."
            className="resize-none text-base" />
        )}

        {current.key === "vibe" && (
          <div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {VIBES.map((v) => (
                <SelectTile key={v.v} active={vibe.includes(v.v)} onClick={() => toggleVibe(v.v)}
                  icon={v.icon} label={v.l} testid={`vibe-${v.v}`} multi />
              ))}
            </div>
            <button data-testid="vibe-not_sure" onClick={() => setVibe(["not_sure"])}
              className={`mt-3 text-sm px-4 py-2 rounded-full border transition-colors ${vibe.includes("not_sure") ? "border-brand-green bg-brand-green text-brand-cream" : "border-brand-cream2 hover:bg-brand-cream2"}`}>
              Not sure — surprise me
            </button>
          </div>
        )}

        {current.key === "avoid" && (
          <Textarea rows={4} data-testid="avoid-input" value={avoid} onChange={(e) => setAvoid(e.target.value)}
            placeholder="e.g. No alcohol, nothing for the kitchen, they already have a lot of candles."
            className="resize-none text-base" />
        )}

        {current.key === "urgency" && (
          <div className="grid grid-cols-2 gap-3">
            {URGENCY.map((u) => (
              <button key={u.v} data-testid={`urgency-${u.v}`} onClick={() => setUrgency(u.v)}
                className={`h-14 rounded-xl border font-medium text-sm transition-all ${urgency === u.v ? "border-brand-green bg-brand-green text-brand-cream" : "border-brand-cream2 bg-white hover:border-brand-green/40"}`}>
                {u.l}
              </button>
            ))}
          </div>
        )}

        {error && <p className="mt-5 text-sm text-brand-burgundy font-medium" data-testid="quiz-error">{error}</p>}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-brand-cream2">
          <Button variant="ghost" onClick={back} disabled={submitting} data-testid="quiz-back-button"
            className="gap-2 hover:bg-brand-cream2">
            <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Home" : "Back"}
          </Button>
          <div className="flex items-center gap-3">
            {current.optional && (
              <Button variant="ghost" onClick={() => { setError(""); step < total - 1 ? setStep(step + 1) : submit(); }}
                disabled={submitting} data-testid="quiz-skip-button" className="text-muted-foreground">
                Skip
              </Button>
            )}
            <Button onClick={next} disabled={submitting} data-testid="quiz-next-button"
              className="bg-brand-green hover:bg-brand-greendark rounded-full px-6 gap-2 min-w-[130px]">
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Finding…</>
              ) : step === total - 1 ? (
                <>Find my gifts <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Continue <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectTile({ active, onClick, icon: Icon, label, testid, multi }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`relative flex flex-col items-center justify-center gap-2 h-24 rounded-xl border transition-all duration-200 ${active ? "border-brand-green bg-brand-green text-brand-cream shadow-md scale-[1.02]" : "border-brand-cream2 bg-white hover:border-brand-green/40 hover:bg-brand-cream2/40"}`}>
      {multi && active && <Check className="w-4 h-4 absolute top-2 right-2" />}
      <Icon className={`w-6 h-6 ${active ? "text-brand-cream" : "text-brand-amber"}`} />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}
